import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button, Alert, Spin } from "antd";
import { useTranslation } from "react-i18next";
import {
  createAlertPackCheckoutSession,
  mountAlertPackEmbeddedCheckout,
} from "util/alertLimits";
import { redirectToStripeCheckout } from "util/stripeCheckout";
import { trackOrgAddonAction } from "util/productAnalytics";
import AlertCardCheckoutFallback from "./AlertCardCheckoutFallback";
import styles from "./alertLimitUpgrade.module.scss";

const AlertLimitUpgradeModal = ({
  open,
  onClose,
  limits,
  returnPath = "/console/alerts",
  source = "unknown",
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("intro");
  const [checkoutMode, setCheckoutMode] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [fallbackNotice, setFallbackNotice] = useState("");
  const [sessionPayload, setSessionPayload] = useState(null);
  const checkoutRef = useRef(null);
  const embeddedCheckoutRef = useRef(null);
  const openTrackedRef = useRef(false);

  const used = limits?.used ?? 0;
  const freeLimit = limits?.free_limit ?? 10;
  const packSize = limits?.paid_pack_size ?? 10;

  const destroyEmbedded = useCallback(() => {
    if (embeddedCheckoutRef.current?.destroy) {
      embeddedCheckoutRef.current.destroy();
      embeddedCheckoutRef.current = null;
    }
  }, []);

  const resetCheckout = useCallback(() => {
    destroyEmbedded();
    setSessionPayload(null);
    setStep("intro");
    setCheckoutMode("loading");
    setErrorMessage("");
    setFallbackNotice("");
  }, [destroyEmbedded]);

  useEffect(() => {
    if (!open) {
      resetCheckout();
      openTrackedRef.current = false;
      return;
    }
    if (!openTrackedRef.current) {
      openTrackedRef.current = true;
      trackOrgAddonAction("modal_opened", { source, returnPath, step: "intro" });
    }
  }, [open, resetCheckout, returnPath, source]);

  const beginStripeCheckout = useCallback(async ({ isRetry = false } = {}) => {
    if (isRetry) {
      trackOrgAddonAction("checkout_retry", {
        source,
        returnPath,
        step: "checkout",
        mode: "fallback",
      });
    }

    setCheckoutMode("loading");
    setFallbackNotice("");
    setErrorMessage("");

    const embeddedRes = await createAlertPackCheckoutSession(returnPath, "embedded");
    if (embeddedRes?.success?.client_secret && embeddedRes.success.publishable_key) {
      setSessionPayload(embeddedRes.success);
      setCheckoutMode("stripe");
      trackOrgAddonAction("checkout_mode", {
        source,
        returnPath,
        step: "checkout",
        mode: "embedded",
      });
      return true;
    }

    const redirectRes = await createAlertPackCheckoutSession(returnPath, "redirect");
    if (redirectRes?.success) {
      try {
        trackOrgAddonAction("checkout_mode", {
          source,
          returnPath,
          step: "checkout",
          mode: "redirect",
        });
        await redirectToStripeCheckout({
          publishableKey: redirectRes.success.publishable_key,
          sessionId: redirectRes.success.session_id,
          checkoutUrl: redirectRes.success.checkout_url,
        });
        return true;
      } catch (err) {
        const message = redirectRes?.error || embeddedRes?.error || err?.message;
        setFallbackNotice(message);
        trackOrgAddonAction("checkout_failed", {
          source,
          returnPath,
          step: "checkout",
          mode: "redirect",
          error: message,
        });
      }
    } else {
      const message =
        redirectRes?.error ||
        embeddedRes?.error ||
        t("alerts_page.upgrade_checkout_failed", "Ödeme başlatılamadı.");
      setFallbackNotice(message);
      trackOrgAddonAction("checkout_failed", {
        source,
        returnPath,
        step: "checkout",
        mode: "redirect",
        error: message,
      });
    }

    setCheckoutMode("fallback");
    trackOrgAddonAction("checkout_mode", {
      source,
      returnPath,
      step: "checkout",
      mode: "fallback",
    });
    return false;
  }, [returnPath, source, t]);

  useEffect(() => {
    if (checkoutMode !== "stripe" || !sessionPayload || !checkoutRef.current) {
      return undefined;
    }

    let cancelled = false;
    destroyEmbedded();
    mountAlertPackEmbeddedCheckout(checkoutRef.current, sessionPayload)
      .then((result) => {
        if (cancelled) return;
        if (result.mode === "embedded") {
          embeddedCheckoutRef.current = result.checkout;
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const message = t("alerts_page.upgrade_checkout_failed", "Ödeme başlatılamadı.");
        setCheckoutMode("fallback");
        setFallbackNotice(message);
        trackOrgAddonAction("checkout_failed", {
          source,
          returnPath,
          step: "checkout",
          mode: "embedded",
          error: err?.message || message,
        });
        trackOrgAddonAction("checkout_mode", {
          source,
          returnPath,
          step: "checkout",
          mode: "fallback",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [checkoutMode, sessionPayload, destroyEmbedded, returnPath, source, t]);

  const handleClose = () => {
    if (open) {
      trackOrgAddonAction("checkout_cancelled", {
        source,
        returnPath,
        step,
        mode: checkoutMode !== "loading" ? checkoutMode : undefined,
      });
    }
    destroyEmbedded();
    onClose();
  };

  const handleProceedToPayment = async () => {
    trackOrgAddonAction("checkout_started", {
      source,
      returnPath,
      step: "intro",
    });
    setLoading(true);
    setStep("checkout");
    await beginStripeCheckout();
    setLoading(false);
  };

  const isCheckoutStep = step === "checkout";

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      destroyOnClose
      width={isCheckoutStep ? 520 : 480}
      className={styles.modal}
      title={t("alerts_page.upgrade_title", "Aylık KPI & alarm add-on")}
    >
      {errorMessage && !isCheckoutStep && (
        <Alert type="error" message={errorMessage} showIcon className={styles.errorAlert} />
      )}

      {!isCheckoutStep && (
        <>
          <p className={styles.lead}>
            {t(
              "alerts_page.upgrade_lead",
              "Organizasyon kotanız doldu (alarm {{used}}/{{freeAlert}}, KPI {{kpiUsed}}/{{freeKpi}}). Aylık add-on ile +{{pack}} KPI ve +{{pack}} alarm ekleyin.",
              {
                free: freeLimit,
                used,
                freeAlert: freeLimit,
                freeKpi: limits?.kpi_free_limit ?? 25,
                kpiUsed: limits?.kpi_used ?? 0,
                pack: packSize,
              },
            )}
          </p>
          <ul className={styles.list}>
            <li>
              {t(
                "alerts_page.upgrade_bullet_free",
                "Temel paket: {{kpi}} KPI + {{alert}} alarm",
                { kpi: limits?.kpi_free_limit ?? 25, alert: freeLimit },
              )}
            </li>
            <li>
              {t(
                "alerts_page.upgrade_bullet_paid",
                "Aylık add-on: +{{count}} KPI ve +{{count}} alarm (birlikte)",
                { count: packSize },
              )}
            </li>
            <li>
              {t(
                "alerts_page.upgrade_bullet_stripe",
                "Güvenli ödeme Stripe ile alınır; istediğiniz zaman iptal edebilirsiniz",
              )}
            </li>
          </ul>
          <div className={styles.actions}>
            <Button onClick={handleClose}>
              {t("alerts_page.upgrade_cancel", "Vazgeç")}
            </Button>
            <Button type="primary" loading={loading} onClick={handleProceedToPayment}>
              {t("alerts_page.upgrade_pay_step", "Ödemeye geç")}
            </Button>
          </div>
        </>
      )}

      {isCheckoutStep && checkoutMode === "loading" && (
        <div className={styles.checkoutLoading}>
          <Spin />
          <p>{t("alerts_page.upgrade_checkout_loading", "Ödeme ekranı yükleniyor…")}</p>
        </div>
      )}

      {isCheckoutStep && checkoutMode === "stripe" && (
        <>
          <div className={styles.checkoutHost} ref={checkoutRef} />
          <div className={styles.checkoutFooter}>
            <Button onClick={handleClose}>
              {t("alerts_page.upgrade_cancel", "Vazgeç")}
            </Button>
          </div>
        </>
      )}

      {isCheckoutStep && checkoutMode === "fallback" && (
        <AlertCardCheckoutFallback
          notice={fallbackNotice}
          onCancel={handleClose}
          onRetry={() => beginStripeCheckout({ isRetry: true })}
        />
      )}
    </Modal>
  );
};

export default AlertLimitUpgradeModal;
